import type { Permission } from 'paintscout'

/**
 * The effective permission set for the `support` role (PaintScout internal staff).
 *
 * Support is admin-equivalent *inside* accounts — their own test/playground account and any
 * account they masquerade into — so they can create, edit, and delete
 * estimates/invoices/contacts/deals/tasks/events and access account settings while
 * troubleshooting. It intentionally mirrors the current `admin` set, and deliberately OMITS
 * `write:account-settings-billing` and `write:account-settings-user-management` so support can
 * never touch billing. Org-level actions (create client, edit billingId, toggle account
 * active/inactive) are gated by ROLE (superadmin) elsewhere, not by these permissions, so
 * support stays restricted there. See issue #21412.
 *
 * This is the single source of truth shared by both the legacy `getPermissions` and the
 * new-role `getPermissionsNext` resolvers, so the two TypeScript paths can never drift apart.
 *
 * !! MUST ALSO BE KEPT IN SYNC with the hand-copied support block in the Auth0 action
 *    devops/auth0/config/actions/update-app-metadata-and-add-to-token.js — that copy is what
 *    actually stamps app_metadata.permissions on login (the action cannot import from the
 *    monorepo). A drift-guard test in get-permissions.spec.ts enforces this. !!
 */
export const SUPPORT_PERMISSIONS: Permission[] = [
  'create:quotes',
  'read:all-quotes',
  'write:all-quotes',
  'delete:all-quotes',
  'send:all-quotes',
  'accept:all-quotes',
  'additional-work:all-quotes',

  'create:contacts',
  'read:all-contacts',
  'write:all-contacts',

  'create:companies',
  'read:all-companies',
  'write:all-companies',

  'create:invoices',
  'read:all-invoices',
  'write:all-invoices',
  'send:all-invoices',

  'create:deals',
  'read:all-deals',
  'write:all-deals',
  'read:assigned-deals',
  'write:assigned-deals',

  'create:tasks',
  'read:all-tasks',
  'write:all-tasks',
  'read:assigned-tasks',
  'write:assigned-tasks',

  // Read-only by design — no write:*-sms permission exists anywhere in the Permission type or
  // the Auth0 action; SMS conversations aren't editable through this permission system.
  'read:all-sms',
  'read:assigned-sms',

  'create:events',
  'read:all-events',
  'write:all-events',
  'read:assigned-events',
  'write:assigned-events',

  'write-presentation-section:all-quotes',

  'write:account-settings',
  'assign:all-documents',
  'accept-payment:all-documents',

  'read:estimator-select'
]
